import { computed } from 'vue';

import ViktorClient from '@/api/clients/viktorClient/ViktorClient';
import { DEFAULT_PER_PAGE } from '@/api/constants';
import { encodeAfURIParams } from '@/helpers/uri';
import { useAuthStore } from '@/stores/auth';
/* TYPES */
import type { PaginatedResponse } from '@/types/api/axios';
import type { Batch, BatchCall, BatchParams, BatchResponse, BatchSummary } from '@/types/batches/batches.types';

import type { ContactsQueryParams } from '../contacts/contacts.types';

// CONTACTS QUERY used for query batches in global mode - takes in the contact table params and reformats them for use in query batch
export function getRecruiterContactsBatchQuery(params: Partial<ContactsQueryParams>): BatchCall {
  delete params.page;
  delete params.per_page;
  delete params.order;

  const authStore = useAuthStore();
  const authUuid = computed(() => authStore.getFullUser.uuid);

  return {
    endpoint: `/viktor/recruiter/${authUuid.value}/contacts`,
    method: 'GET',
    data: [{ params: encodeAfURIParams(params) }]
  };
}

/**
 * Allows to fetch current batches for a specific user
 */
export function getCurrentBatches(page: number): Promise<PaginatedResponse<Batch[]>> {
  const authStore = useAuthStore();
  const authUuid = computed(() => authStore.getFullUser.uuid);

  const params = {
    'created_by:uuid': authUuid.value,
    order: 'created_at:desc',
    page,
    per_page: DEFAULT_PER_PAGE
  };

  return ViktorClient().get('/batches', { params });
}

/**
 * Allows to fetch recap info on ongoing batches for the current user
 */
export function getCurrentBatchesSummary(): Promise<BatchSummary> {
  const authStore = useAuthStore();
  const authUuid = computed(() => authStore.getFullUser.uuid);

  const params = {
    'created_by:uuid': authUuid.value
  };
  return ViktorClient().get('/batches-summary', { params });
}

/* POST */
export function postBatch({ query, action }: { query?: BatchCall; action: BatchCall }): Promise<BatchResponse> {
  const params: BatchParams = {
    query,
    action
  };

  return ViktorClient().post('/batch', params);
}

/* PATCH */
export function retryBatch(uuid): Promise<void> {
  return ViktorClient().patch(`/batch/${uuid}/retry`);
}

/* DELETE */
export function deleteBatch(uuid): Promise<void> {
  return ViktorClient().delete(`/batch/${uuid}`);
}
